import * as React from "react";
import {useEffect, useState} from "react";
import { Paper, Box, Stack, List, ListItem, Typography, ListItemButton, Avatar } from "@mui/material";
import { HeaderNavProps } from "@/components/header/common/types";
import { toggleNavigationPane } from "@/components/header/common/utils";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

type SecondaryNavigationPaneProps = {
  secondaryNavItems: HeaderNavProps[];
  setTertiaryNavItems: (destination: HeaderNavProps[]) => void;
  setTertiaryHeaderLabel: (label: string) => void;
  setTertiaryHeaderLearnMoreUrl: (url: string) => void;
  setTertiaryHeaderViewAllDestinationsUrl: (url: string) => void;
};
export default function SecondaryPane(props: SecondaryNavigationPaneProps) {
  const { secondaryNavItems, setTertiaryNavItems, setTertiaryHeaderLabel, setTertiaryHeaderLearnMoreUrl, setTertiaryHeaderViewAllDestinationsUrl } = props;
  const [prevSelected, setPrevSelected] = useState(secondaryNavItems[0]);

  const handleListItemClick = (d: HeaderNavProps) => {
    toggleNavigationPane();
    setPrevSelected(d);
    setTertiaryHeaderLearnMoreUrl(d.fields.learnMoreUrl);
    setTertiaryHeaderViewAllDestinationsUrl(d.fields.viewAllDestinationsUrl);
    setTertiaryHeaderLabel(d.fields.title);
    setTertiaryNavItems(d.fields.subNavList ?? []);
  };

  useEffect(() => {
    setPrevSelected(secondaryNavItems[0]);
  }, [secondaryNavItems]);

  return (
    <Paper
      sx={{
        backgroundColor: "background.default",
        height: { sm: "calc(100dvh - var(--Header-height))", md: "100dvh" },
        overflow: "hidden",
        width: "100%",
        maxHeight: "inherit",
        borderBottomLeftRadius: "8px"
      }}
    >
      <List
        sx={{
          "--ListItem-paddingY": "0.75rem"
        }}
      >
        {secondaryNavItems.map((destination: HeaderNavProps) => (
          <Box
            key={destination.sys.id}
            sx={{
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`
            }}
          >
            <ListItem 
              key={destination.sys.id} 
              data-testid={`secondary-nav-item-${destination.fields.title}`}
              sx={{
                padding: 0,
                "&:hover, &.Mui-selected": {
                  "& .MuiTypography-root": {
                    fontWeight: "600"
                  }
                }
              }}
            >
              <ListItemButton
                onClick={() => {
                  handleListItemClick({ ...destination });
                }}
                selected={prevSelected?.sys?.id === destination.sys.id}
                color="neutral"
                sx={{
                  flexDirection: "column",
                  alignItems: "initial",
                  gap: 1,
                  opacity: .7,
                  "&:hover, &.Mui-selected": {
                    "&::before": {
                      content: "\"\"",
                      display: "fill",
                      width: "8px",
                      height: "auto",
                      backgroundColor: "secondary.main",
                      position: "absolute",
                      left: 0,
                      top:0,
                      bottom: 0
                    },
                    "& .MuiTypography-root": {
                      color: "text.primary",
                      fontWeight: "700"
                    },
                    opacity: 1
                  }
                }}
                className="navSecondary"
                data-nav-level="secondary"
                data-nav-label={destination.fields.title}
              >
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    sx={{
                      p: .5
                    }}
                  >
                    {destination.fields.icon?.fields.file.url && (
                      <Avatar
                        alt={destination.fields.title}
                        src={`https://${destination.fields.icon?.fields.file.url}`}
                        sx={{ width: "2.5rem", height: "2.5rem" }}
                      />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: "600",
                          pl: .5
                        }}
                      >
                        {destination.fields.title}
                      </Typography>
                    </Box>
                  </Stack>
                  <KeyboardArrowRightIcon
                    sx={{
                      fontSize: "2rem",
                      fontWeight: "600"
                    }}
                  />
                </Stack>
              </ListItemButton>
            </ListItem>
          </Box>
        ))}
      </List>
    </Paper>
  );
}
